import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  People,
  Hotel,
  Assessment,
  Download,
  Print,
  DateRange,
  CalendarToday,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const revenueData = [
  { month: 'Jan', revenue: 45000, expenses: 28000, profit: 17000 },
  { month: 'Feb', revenue: 52000, expenses: 31000, profit: 21000 },
  { month: 'Mar', revenue: 48000, expenses: 29000, profit: 19000 },
  { month: 'Apr', revenue: 61000, expenses: 35000, profit: 26000 },
  { month: 'May', revenue: 55000, expenses: 32000, profit: 23000 },
  { month: 'Jun', revenue: 67000, expenses: 38000, profit: 29000 },
];

const occupancyData = [
  { month: 'Jan', rate: 72 },
  { month: 'Feb', rate: 78 },
  { month: 'Mar', rate: 75 },
  { month: 'Apr', rate: 85 },
  { month: 'May', rate: 82 },
  { month: 'Jun', rate: 92 },
];

const roomTypeRevenue = [
  { name: 'Deluxe', value: 125000, color: '#1976d2' },
  { name: 'Suite', value: 98000, color: '#dc004e' },
  { name: 'Standard', value: 76000, color: '#9c27b0' },
];

const departmentData = [
  { dept: 'Rooms', revenue: 180000 },
  { dept: 'Restaurant', revenue: 45000 },
  { dept: 'Spa', revenue: 28000 },
  { dept: 'Laundry', revenue: 12000 },
  { dept: 'Minibar', revenue: 8000 },
];

const topPerformers = [
  { name: 'Maria Garcia', department: 'Housekeeping', rating: 4.9, tasks: 156 },
  { name: 'John Smith', department: 'Maintenance', rating: 4.8, tasks: 89 },
  { name: 'Sara White', department: 'Front Desk', rating: 4.9, tasks: 234 },
  { name: 'Lisa Brown', department: 'Kitchen', rating: 4.7, tasks: 178 },
];

export default function Reports() {
  const [activeTab, setActiveTab] = React.useState(0);
  const [dateRangeOpen, setDateRangeOpen] = React.useState(false);
  const [dateRange, setDateRange] = React.useState({
    startDate: '2025-11-01',
    endDate: '2025-11-16',
  });
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const handleApplyDateRange = () => {
    setSnackbar({
      open: true,
      message: `Date range applied: ${new Date(dateRange.startDate).toLocaleDateString()} - ${new Date(dateRange.endDate).toLocaleDateString()}`,
      severity: 'success',
    });
    setDateRangeOpen(false);
  };

  const stats = [
    { label: 'Total Revenue', value: '$328,000', change: '+12.5%', color: '#1976d2', icon: AttachMoney },
    { label: 'Avg Occupancy', value: '81%', change: '+5.4%', color: '#2e7d32', icon: Hotel },
    { label: 'Total Guests', value: '1,248', change: '+8.2%', color: '#9c27b0', icon: People },
    { label: 'Avg Daily Rate', value: '$185', change: '+3.1%', color: '#ed6c02', icon: TrendingUp },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Reports & Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive hotel performance metrics and insights
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Download />} size="small">
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />} size="small">
            Print
          </Button>
          <Button 
            variant="contained" 
            startIcon={<DateRange />} 
            size="small"
            onClick={() => setDateRangeOpen(true)}
          >
            Date Range
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        {stat.change} vs last month
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Revenue Analysis" />
          <Tab label="Occupancy Trends" />
          <Tab label="Department Performance" />
          <Tab label="Staff Performance" />
        </Tabs>

        <Box p={3}>
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Revenue vs Expenses vs Profit
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#2e7d32" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stroke="#1976d2" fill="url(#colorRevenue)" name="Revenue" />
                    <Area type="monotone" dataKey="profit" stroke="#2e7d32" fill="url(#colorProfit)" name="Profit" />
                    <Line type="monotone" dataKey="expenses" stroke="#dc004e" strokeWidth={2} name="Expenses" />
                  </AreaChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Revenue by Room Type
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={roomTypeRevenue}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: $${(value / 1000).toFixed(0)}k`}
                      outerRadius={110}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {roomTypeRevenue.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Monthly Occupancy Rate Trends
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={occupancyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="rate" stroke="#2e7d32" strokeWidth={3} name="Occupancy %" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Department Revenue Breakdown
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={departmentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dept" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="revenue" fill="#1976d2" radius={[8, 8, 0, 0]} name="Revenue ($)" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {activeTab === 3 && (
            <TableContainer>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Top Performing Staff Members
              </Typography>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell><strong>Rating</strong></TableCell>
                    <TableCell><strong>Tasks Completed</strong></TableCell>
                    <TableCell><strong>Performance</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topPerformers.map((staff, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                            {staff.name[0]}
                          </Avatar>
                          {staff.name}
                        </Box>
                      </TableCell>
                      <TableCell>{staff.department}</TableCell>
                      <TableCell>
                        <Chip label={`⭐ ${staff.rating}`} size="small" color="warning" />
                      </TableCell>
                      <TableCell><strong>{staff.tasks}</strong></TableCell>
                      <TableCell>
                        <Chip label="Excellent" size="small" color="success" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Date Range Dialog */}
      <Dialog open={dateRangeOpen} onClose={() => setDateRangeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <CalendarToday color="primary" />
            <Typography variant="h6">Select Date Range</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Range: {new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const lastWeek = new Date(today);
                      lastWeek.setDate(today.getDate() - 7);
                      setDateRange({
                        startDate: lastWeek.toISOString().split('T')[0],
                        endDate: today.toISOString().split('T')[0],
                      });
                    }}
                  >
                    Last 7 Days
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const lastMonth = new Date(today);
                      lastMonth.setDate(today.getDate() - 30);
                      setDateRange({
                        startDate: lastMonth.toISOString().split('T')[0],
                        endDate: today.toISOString().split('T')[0],
                      });
                    }}
                  >
                    Last 30 Days
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                      setDateRange({
                        startDate: startOfMonth.toISOString().split('T')[0],
                        endDate: today.toISOString().split('T')[0],
                      });
                    }}
                  >
                    This Month
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                      setDateRange({
                        startDate: lastMonthStart.toISOString().split('T')[0],
                        endDate: lastMonthEnd.toISOString().split('T')[0],
                      });
                    }}
                  >
                    Last Month
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const startOfYear = new Date(today.getFullYear(), 0, 1);
                      setDateRange({
                        startDate: startOfYear.toISOString().split('T')[0],
                        endDate: today.toISOString().split('T')[0],
                      });
                    }}
                  >
                    This Year
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateRangeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyDateRange} startIcon={<CalendarToday />}>
            Apply Date Range
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
